import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import dayjs from 'dayjs';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { ActivationStatus, TaskStatus } from '@/entities/vfo/types';
import { UserCell } from '@/features/vfo/components';
import { VfoFileIcon } from '@/features/vfo/components/VfoFileIcon';
import { MAX_TASK_FILES } from '@/features/vfo/constants';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { useOpenDocument } from '@/features/vfo/hooks/useOpenDocument';
import { useTaskAttachments } from '@/features/vfo/hooks/useTaskAttachments';
import { useTaskPriorities } from '@/features/vfo/hooks/useTaskPriorities';
import { useTasks } from '@/features/vfo/hooks/useTasks';
import { COLORS } from '@/shared/constants/colors';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { DropdownMenu } from '@/shared/uikit/DropdownMenu';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { dates } from '@/shared/utils/dates';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { EditTaskModalProps } from '@/widgets/modals/types';

import { VfoModalContainerV3 } from '../containers/VfoModalContainerV3';

import { EditButtons } from './EditButtons';
import {
  AttachmentsWrapper,
  Content,
  Count,
  DatePickerStyled,
  DeleteButton,
  DocumentTitle,
  DropdownMenuStyled,
  DropdownWrapper,
  Field,
  FileTypeWrapper,
  FileWrapper,
  InfoColumn,
  InputStyled,
  InputWrapper,
  Label,
  Row,
  RowText,
  SelectPriorityStyled,
  SelectStyled,
  TasksList,
  Text,
  TextareaStyled,
  TextWrapper,
  TitleStyled,
} from './styled';
import { useDocumentActions } from './useDocumentActions';

export const EditTaskModal: FC<EditTaskModalProps> = ({ taskId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'editTask' });
  const { status } = useI18nValues();
  const modals = useModalsStore();

  const { cases, findCase } = useCases();
  const { openTaskAttachments } = useTaskAttachments();
  const { members, currentUser, getByProfileId } = useMembers();
  const { findById, updateTask, addTaskDocuments } = useTasks();
  const isFounder = useIsFounder();
  const { getPriority } = useTaskPriorities();
  const openDocument = useOpenDocument();

  const taskData = findById(taskId);
  const caseData = findCase(taskData?.caseId);
  const creatorData = taskData?.creator.profileId
    ? getByProfileId(taskData?.creator.profileId)
    : null;
  const assigneeData = taskData?.assignee?.profileId
    ? getByProfileId(taskData?.assignee?.profileId)
    : null;

  const isCreator = currentUser?.profileId === taskData?.creator.profileId;
  const isAssignee = taskData?.assignee?.profileId === currentUser?.profileId;

  const canEdit = isFounder || isCreator || isAssignee;
  const canDelete = isFounder || isCreator;
  const taskAttachments = taskData?.documents ?? [];

  const { sm } = useBreakpoints();
  const { getActions } = useDocumentActions({ taskId, canEdit });

  const [showTitleInput, setShowTitleInput] = useState(false);
  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const [titleValue, setTitleValue] = useState(taskData?.title ?? '');
  const [descriptionValue, setDescriptionValue] = useState(taskData?.description ?? '');

  const onChangeStatus = (newStatus: TaskStatus) => {
    if (!taskData?.id) return;
    updateTask(taskData?.id, { status: newStatus });
  };

  const handleSaveTitle = () => {
    if (!taskData?.id) return;
    updateTask(taskData?.id, { title: titleValue });
    setShowTitleInput(false);
  };

  const handleCancelTitle = () => {
    setTitleValue(taskData?.title ?? '');
    setShowTitleInput(false);
  };

  const handleSaveDescription = () => {
    if (!taskData?.id) return;
    updateTask(taskData?.id, { description: descriptionValue });
    setShowDescriptionInput(false);
  };

  const handleCancelDescription = () => {
    setDescriptionValue(taskData?.description ?? '');
    setShowDescriptionInput(false);
  };

  const handleDeleteClick = () => {
    modals.openModal({
      type: 'DeleteTaskModal',
      taskId,
      onDelete: modals.closeModal,
    });
  };

  const handleAddFile = () => {
    if (!taskData) return;

    openTaskAttachments({
      caseId: taskData.caseId,
      taskAttachments,
      onSave: async (documents) => {
        const documentIds = documents.map((doc) => doc.id);
        await addTaskDocuments(taskId, documentIds);
      },
    });
  };

  const availableCases = useMemo(() => {
    if (!taskData?.assignee) {
      return cases;
    }

    return cases.filter((item) =>
      item.members.some((m) => m.profileId === taskData?.assignee?.profileId),
    );
  }, [taskData?.assignee]);

  const availableAssignees = useMemo(() => {
    const arr = members.filter(
      (item) =>
        item.activationStatus === ActivationStatus.ACTIVE &&
        caseData?.members.some((member) => member.profileId === item.profileId),
    );

    const options: { label: ReactNode; value?: number | null }[] = arr.map((item) => ({
      label: <UserCell member={item} clickable={false} showStatus={true} />,
      value: item.profileId,
    }));

    if (taskData?.assignee) {
      options.unshift({
        label: t('none'),
        value: null,
      });
    }

    return options;
  }, [members, caseData, taskData?.assignee]);

  const statusOptions = useMemo(() => {
    return [
      {
        label: status(TaskStatus.TODO),
        value: TaskStatus.TODO,
        onClick: () => onChangeStatus(TaskStatus.TODO),
      },
      {
        label: status(TaskStatus.IN_PROGRESS),
        value: TaskStatus.IN_PROGRESS,
        onClick: () => onChangeStatus(TaskStatus.IN_PROGRESS),
      },
      {
        label: status(TaskStatus.DONE),
        value: TaskStatus.DONE,
        onClick: () => onChangeStatus(TaskStatus.DONE),
      },
    ].filter((item) => item.value !== taskData?.status);
  }, [taskData?.status]);

  const titleEditBlock = showTitleInput ? (
    <InputWrapper>
      <InputStyled
        maxLength={255}
        autoFocus
        value={titleValue}
        onChange={(e) => setTitleValue(e.target.value)}
      />
      <EditButtons
        disabled={titleValue.length === 0}
        onSave={handleSaveTitle}
        onCancel={handleCancelTitle}
      />
    </InputWrapper>
  ) : (
    <TitleStyled
      bold
      size={18}
      onClick={() => {
        if (!canEdit) return;
        setShowTitleInput(true);
      }}
    >
      {taskData?.title}
    </TitleStyled>
  );

  const priorityField = canEdit ? (
    <SelectPriorityStyled
      value={taskData?.priority}
      suffixIcon={<Icon name="caret-down" size="s16" />}
      popupMatchSelectWidth={false}
      onChange={(priority) => {
        if (!taskData?.id) return;
        updateTask(taskData?.id, { priority });
      }}
    />
  ) : (
    taskData?.priority && getPriority(taskData?.priority)?.label
  );

  const deleteButton = (
    <DeleteButton
      size="middle"
      type="text"
      icon={<Icon name="trash" size="s20" />}
      onClick={handleDeleteClick}
    >
      {t('delete')}
    </DeleteButton>
  );

  return (
    <VfoModalContainerV3 title={taskData?.taskNumber ?? ''}>
      <Content>
        <TextWrapper className="uikitScrollbar">
          {!sm && titleEditBlock}

          {showDescriptionInput ? (
            <InputWrapper>
              <TextareaStyled
                autoFocus
                value={descriptionValue}
                onChange={(e) => setDescriptionValue(e.target.value)}
              />
              <EditButtons
                disabled={descriptionValue.length === 0}
                onSave={handleSaveDescription}
                onCancel={handleCancelDescription}
              />
            </InputWrapper>
          ) : (
            <Text
              onClick={() => {
                if (!canEdit) return;
                setShowDescriptionInput(true);
              }}
            >
              {taskData?.description}
            </Text>
          )}
          <AttachmentsWrapper>
            <Row>
              <RowText>
                <span>{t('attachments')}</span>
                {taskAttachments.length > 0 && <Count>({taskAttachments.length})</Count>}
              </RowText>
              <Button
                type="text"
                size="small"
                icon={<Icon name="plus" size="s20" />}
                style={{ height: 20 }}
                onClick={handleAddFile}
                disabled={taskAttachments.length >= MAX_TASK_FILES}
              >
                {t('addFileBtn')}
              </Button>
            </Row>
            <TasksList>
              {taskAttachments.map((doc) => (
                <FileWrapper onClick={() => openDocument(doc)} key={doc.id}>
                  <FileTypeWrapper className={cn({ isExternal: doc.source === 'EXTERNAL' })}>
                    <VfoFileIcon file={doc} size="s20" />
                  </FileTypeWrapper>
                  <DocumentTitle level={5} size={14} bold>
                    {doc.title}
                  </DocumentTitle>
                  <DropdownMenu
                    placement="bottomLeft"
                    overlayStyle={{ width: 250 }}
                    items={getActions(doc)}
                  />
                </FileWrapper>
              ))}
            </TasksList>
          </AttachmentsWrapper>
        </TextWrapper>
        <InfoColumn className="uikitScrollbar">
          <DropdownWrapper>
            {sm && priorityField}
            {taskData?.status && (
              <DropdownMenuStyled disabled={!canEdit} items={statusOptions}>
                <Button
                  style={{ width: '100%' }}
                  type="default"
                  icon={<Icon name="caret-down" style={{ color: COLORS.systemDark200 }} />}
                  iconPosition="end"
                >
                  {status(taskData.status)}
                </Button>
              </DropdownMenuStyled>
            )}
          </DropdownWrapper>
          {sm ? (
            titleEditBlock
          ) : (
            <Field>
              <Label>{t('priority')}</Label>
              {priorityField}
            </Field>
          )}
          <Field>
            <Label>{t('dueDate')}</Label>
            {canEdit ? (
              <DatePickerStyled
                inputReadOnly
                format={'MMMM DD, YYYY'}
                minDate={dayjs(new Date())}
                allowClear={{ clearIcon: <Icon name="x-circle" size="s16" /> }}
                value={taskData?.dueDate}
                onChange={(dueDate) => {
                  if (!taskData?.id) return;
                  updateTask(taskData?.id, { dueDate });
                }}
              />
            ) : (
              <span>{taskData?.dueDate && dates.formatLongDate2(taskData?.dueDate)}</span>
            )}
          </Field>
          <Field>
            <Label>{t('case')}</Label>
            {canEdit ? (
              <SelectStyled
                options={availableCases.map((item) => ({
                  label: item.title,
                  value: item.id,
                }))}
                className="caseSelect"
                value={taskData?.caseId}
                onChange={(caseId) => {
                  if (!taskData?.id) return;
                  updateTask(taskData?.id, { caseId });
                }}
              />
            ) : (
              <span>{caseData?.title}</span>
            )}
          </Field>
          <Field>
            <Label>{t('assignee')}</Label>
            {canEdit ? (
              <SelectStyled
                options={availableAssignees}
                value={taskData?.assignee?.profileId ?? t('none')}
                popupMatchSelectWidth={false}
                onChange={async (profileId) => {
                  if (!taskData?.id) return;
                  if (!profileId) {
                    updateTask(taskData?.id, { assigneeProfileId: null });
                    return;
                  }
                  updateTask(taskData?.id, {
                    assigneeProfileId: profileId,
                    caseId: taskData.caseId,
                  });
                }}
              />
            ) : (
              assigneeData && <UserCell member={assigneeData} clickable={false} showStatus={true} />
            )}
          </Field>
          {creatorData && (
            <Field>
              <Label>{t('creator')}</Label>
              <UserCell member={creatorData} clickable={false} showStatus={true} />
            </Field>
          )}
          {taskData?.createdAt && (
            <Field>
              <Label>{t('createdDate')}</Label>
              <span>{dates.formatLongDate2(taskData?.createdAt)}</span>
            </Field>
          )}
          {canDelete && !sm && deleteButton}
        </InfoColumn>
      </Content>
      {canDelete && sm && deleteButton}
    </VfoModalContainerV3>
  );
};
